import React, { FunctionComponent, useEffect, useRef, useState, useCallback } from 'react';
import './PageStepper.scss';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../../../hooks/useIsMobile';
import RightArrowIcon from '../../../assets/images/icons/right-arrow-icon.svg';
import LeftArrowIcon from '../../../assets/images/icons/left-arrow-icon.svg';

export type GetCareStep = {
  label: string;
  show: boolean;
};

export const TAB_BREAKPOINT = 767;

type Props = {
  steps: GetCareStep[];
  activeStep: number;
  completedSteps: number[];
  lastEditedStep: number | null | undefined;
  onStepClick?: (index: number) => void;
};

const PageStepper: FunctionComponent<Props> = ({
  steps,
  activeStep,
  completedSteps,
  lastEditedStep,
  onStepClick,
}) => {
  const { t } = useTranslation();
  const stepperRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const isMobile = useIsMobile(TAB_BREAKPOINT);

  const checkScrollPosition = useCallback(() => {
    if (stepperRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = stepperRef.current;
      setShowLeftArrow(scrollLeft > 5);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
    }
  }, []);

  useEffect(() => {
    const activeStepElement = stepperRef.current?.querySelector('.step-item.active');
    if (activeStepElement) {
      activeStepElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
    checkScrollPosition();
  }, [activeStep, checkScrollPosition]);

  useEffect(() => {
    const stepperElement = stepperRef.current;
    if (stepperElement) {
      const resizeObserver = new ResizeObserver(checkScrollPosition);
      resizeObserver.observe(stepperElement);
      return () => resizeObserver.disconnect();
    }
  }, [checkScrollPosition]);

  const handleScroll = useCallback((direction: 'left' | 'right') => {
    if (stepperRef.current) {
      const scrollAmount = stepperRef.current.clientWidth / 2;
      stepperRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  }, []);

  const isStepEnabled = useCallback(
    (stepIndex: number) => {
      if (stepIndex <= activeStep) return true;
      if (lastEditedStep && lastEditedStep >= 0 && stepIndex > lastEditedStep) return false;
      return completedSteps.includes(stepIndex);
    },
    [activeStep, completedSteps, lastEditedStep],
  );

  const visibleSteps = steps.filter((step) => step.show);

  return (
    <div className='stepper-wrapper'>
      {!isMobile && showLeftArrow && (
        <button className='scroll-arrow left' onClick={() => handleScroll('left')}>
          <img src={LeftArrowIcon} alt='left-arrow' />
        </button>
      )}
      <div
        className='stepper-container'
        ref={stepperRef}
        onScroll={checkScrollPosition}
        style={
          {
            '--active-step': activeStep,
            '--total-steps': visibleSteps.length,
          } as React.CSSProperties
        }
      >
        {visibleSteps.map((step, index) => {
          const stepIndex = steps.findIndex((s) => s === step);
          const isEnabled = isStepEnabled(stepIndex);
          return (
            <button
              key={stepIndex}
              className={`step-item ${stepIndex === activeStep ? 'active' : ''} ${
                !isEnabled ? 'disabled' : ''
              } ${index === 0 ? 'first-visible' : ''} ${
                index === visibleSteps.length - 1 ? 'last-visible' : ''
              }`}
              onClick={() => isEnabled && onStepClick && onStepClick(stepIndex)}
              disabled={!isEnabled}
            >
              {t(step.label)}
            </button>
          );
        })}
      </div>
      {!isMobile && showRightArrow && (
        <button className='scroll-arrow right' onClick={() => handleScroll('right')}>
          <img src={RightArrowIcon} alt='right-arrow' />
        </button>
      )}
    </div>
  );
};

export default PageStepper;
